import { createFileRoute } from '@tanstack/react-router';
import { Button, Typography, Box, CircularProgress } from '@mui/material';
import { useCallback, useState, useEffect } from 'react';
import { useGetAdminPortalSession, useSendAdminPortalMagicLink } from '@amber-ui/core';

export const Route = createFileRoute('/_auth/sso/admin')({
  component: SSOAdminPage,
});

function SSOAdminPage() {
  const { loading, stytch_public_token, error: queryError } = useGetAdminPortalSession();
  const { sendAdminPortalMagicLink } = useSendAdminPortalMagicLink();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Store the public token in localStorage so downstream SSO routes can access it
  // (authenticate.tsx and portal.tsx read it from localStorage since they're public routes
  // without access to the authenticated GraphQL context)
  useEffect(() => {
    if (stytch_public_token) {
      localStorage.setItem('stytch_public_token', stytch_public_token);
    }
  }, [stytch_public_token]);

  // React Compiler note: the try/catch with ternary inside this async useCallback handler
  // does NOT cause compiler bail-out. The compiler only bails on try/catch value blocks in the
  // synchronous render path. Async callbacks inside useCallback are not analyzed as render-path code.
  const handleSendMagicLink = useCallback(async () => {
    setError(null);
    setSending(true);
    try {
      const result = await sendAdminPortalMagicLink();
      if (result.success) {
        setSent(true);
      } else {
        setError(result.error_message || 'Failed to send magic link');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send magic link';
      setError(message);
    }
    setSending(false);
  }, [sendAdminPortalMagicLink]);

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {queryError.message || 'You do not have access to SSO administration.'}
        </Typography>
        <Button variant="outlined" href="/grain_bin/dashboard">
          Return to Dashboard
        </Button>
      </Box>
    );
  }

  if (sent) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Check Your Email
        </Typography>
        <Typography>
          We've sent a magic link to your email address. Click the link to access the SSO
          configuration portal.
        </Typography>
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>
          The link expires in 60 minutes.
        </Typography>
        <Button variant="outlined" sx={{ mt: 3 }} href="/grain_bin/dashboard">
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        SSO Configuration
      </Typography>
      <Typography sx={{ mb: 3 }}>
        Configure your organization's Single Sign-On (SSO) identity provider settings.
      </Typography>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleSendMagicLink}
        disabled={sending}
      >
        {sending ? 'Sending...' : 'Configure SSO'}
      </Button>
      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
